import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from '../../store'
import { ProductDetails } from './ProductDetails/ProductDetails'
import { AboutAgreement } from './AboutAgreement/AboutAgreement'
import { ProductDimensions } from '../../components/PrivateOfferForm/DetailedOffer/ProductDimensions/ProductDimensions'
import { getPricesArray } from '../../modules/resaleAuthorization/utils/getPricesArray'
import { Installments } from './Installments/Installments'
import { LicenseAgreement } from './LicenseAgreement/LicenseAgreement'
import { useParams, useLocation } from 'react-router-dom'
import {
  CPPOResellerAgreementState,
  getCppoAgreementData,
} from '../../modules/cppoResellerAgreement'
import { isLoading, LoadingTypes } from '@labrav/common'
import { useStyles } from './index.styles'
import { get, isEmpty } from 'lodash'
import { PoweredByLabra } from './PoweredByLabra/PoweredByLabra'
import { Box } from '@labrav/react-components'
import type { Dimension } from '../../modules/privateOffer/types'

const CPPOResellerAgreement = () => {
  const { classes } = useStyles()
  const { secureKey, partnerId, crmAwsResellerAgreementId } = useParams<{
    secureKey: string
    partnerId: string
    crmAwsResellerAgreementId: string
  }>()
  const { search } = useLocation()
  const email = new URLSearchParams(search).get('email')?.toString()
  const agreementState = useSelector<Partial<CPPOResellerAgreementState>>(
    state => state.cppoResellerAgreement
  )
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const dispatch = useDispatch()
  const [agreementError, setAgreementError] = useState<string>()
  const agreementRequestRef = useRef(0)

  useEffect(() => {
    if (!secureKey || !partnerId || !crmAwsResellerAgreementId) {
      return
    }

    const requestId = ++agreementRequestRef.current
    setAgreementError(undefined)
    dispatch(
      getCppoAgreementData(
        partnerId,
        crmAwsResellerAgreementId,
        secureKey,
        email,
        {
          isCurrent: () => agreementRequestRef.current === requestId,
        }
      )
    ).catch((error: unknown) => {
      if (agreementRequestRef.current !== requestId) return
      setAgreementError(
        error instanceof Error
          ? error.message
          : 'Unable to fetch agreement information'
      )
    })

    return () => {
      agreementRequestRef.current += 1
    }
  }, [secureKey, partnerId, crmAwsResellerAgreementId, email])

  if (agreementError) {
    return (
      <div
        className={classes.root}
        data-testid="cppo-agreement-error"
        role="alert"
      >
        {agreementError}
      </div>
    )
  }

  if (
    loading ||
    !partnerId ||
    !secureKey ||
    !crmAwsResellerAgreementId ||
    isEmpty(agreementState)
  ) {
    return <div data-testid="cppo-agreement-loading"></div>
  }
  const dimensions = get(
    agreementState,
    'products[0].dimensions',
    []
  ) as Dimension[]
  const pricesArray = getPricesArray(
    get(agreementState, 'products[0].dimensions', []) as Dimension[]
  )
  return (
    <div className={classes.root} data-testid="cppo-agreement">
      <div className={classes.content}>
        <ProductDetails />
        <AboutAgreement />
        <Box heading={'Product dimensions'} subHeading={''}>
          <ProductDimensions
            dimensions={dimensions}
            hasFlexiblePayment={agreementState.flexiblePaymentTerms}
            pricesArray={pricesArray}
            isTypeCPPO={true}
          />
        </Box>
        <Installments />
        <LicenseAgreement />
        <div className={classes.poweredByLabra}>
          <PoweredByLabra />
        </div>
      </div>
    </div>
  )
}

export default CPPOResellerAgreement
